import { RuleObject } from 'antd/lib/form'

export const MIN_LEN_PASSWORD = 15
export const PASSWORD_RULES_HINT_ITEMS = [
  'Не короче 15 символов',
  'Строчные и прописные латинские буквы',
  'Минимум одна цифра',
  'Минимум один спецсимвол',
  'Только латинские буквы, цифры и спецсимволы без пробелов',
  'Не должен содержать личную информацию',
  'Не должен совпадать с предыдущими паролями',
  'Без словарных слов, повторов и последовательностей вроде 1234 или qwert',
]

export type PasswordRuleStatus = {
  label: string
  isValid: boolean
}

const PASSWORD_FORBIDDEN_WORDS = [
  'admin',
  'administrator',
  'default',
  'guest',
  'login',
  'password',
  'pass',
  'passw',
  'qwerty',
  'test',
  'user',
  'welcome',
  'worldskills',
  'reaskills',
]

const PASSWORD_SEQUENCES = [
  '1234567890',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  'abcdefghijklmnopqrstuvwxyz',
  '0123456789',
]

const PASSWORD_SPECIAL_CHAR_REGEX = /[!-/:-@[-`{-~]/

const LEET_CHARS: Record<string, string> = {
  '@': 'a',
  '4': 'a',
  $: 's',
  '5': 's',
  '0': 'o',
  '1': 'i',
  '!': 'i',
  '3': 'e',
  '7': 't',
}

const normalizeLeetPassword = (value: string): string =>
  value
    .toLowerCase()
    .split('')
    .map((char) => LEET_CHARS[char] || char)
    .join('')

const hasOrderedSequence = (value: string): boolean =>
  PASSWORD_SEQUENCES.some((sequence) => {
    const sequences = [sequence, sequence.split('').reverse().join('')]
    return sequences.some((currentSequence) => {
      for (let size = 4; size <= currentSequence.length; size++) {
        for (let index = 0; index <= currentSequence.length - size; index++) {
          if (value.includes(currentSequence.slice(index, index + size))) {
            return true
          }
        }
      }
      return false
    })
  })

const hasRepeatedGroup = (value: string): boolean => {
  for (let size = 2; size <= 4; size++) {
    const regex = new RegExp(`(.{${size}})\\1+`)
    if (regex.test(value)) {
      return true
    }
  }
  return false
}

export const getPasswordRuleStatuses = (value = ''): PasswordRuleStatus[] => {
  const normalized = value.toLowerCase()
  const normalizedLeet = normalizeLeetPassword(value)
  const hasForbiddenWord = PASSWORD_FORBIDDEN_WORDS.some(
    (word) => normalized.includes(word) || normalizedLeet.includes(word),
  )
  const hasRepeatedChars = /(.)\1{2,}/.test(normalized)
  const hasInvalidChars = /\s/.test(value) || /[^A-Za-z0-9!-/:-@[-`{-~]/.test(value)

  return [
    {
      label: 'Не меньше 15 символов',
      isValid: value.length >= MIN_LEN_PASSWORD,
    },
    {
      label: 'Строчная буква',
      isValid: /[a-z]/.test(value),
    },
    {
      label: 'Прописная буква',
      isValid: /[A-Z]/.test(value),
    },
    {
      label: 'Цифра',
      isValid: /\d/.test(value),
    },
    {
      label: 'Спецсимвол',
      isValid: PASSWORD_SPECIAL_CHAR_REGEX.test(value),
    },
    {
      label: 'Только латиница',
      isValid: !!value && !hasInvalidChars,
    },
    {
      label: 'Без словарных или повторяющихся слов',
      isValid:
        !!value &&
        !hasForbiddenWord &&
        !hasRepeatedChars &&
        !hasRepeatedGroup(normalized) &&
        !hasOrderedSequence(normalized),
    },
  ]
}

export const isPasswordValid = (value = ''): boolean => getPasswordRuleStatuses(value).every((item) => item.isValid)

export const onlyRuOrEnCharsValidator = (rule: RuleObject, value: string): Promise<void> => {
  const regex = new RegExp(/^[а-яА-ЯёЁa-zA-Z-]*$/)
  return regex.test(value) ? Promise.resolve() : Promise.reject('Нельзя использовать спецсимволы и цифры')
}

export const emailValidator = (rule: RuleObject, value: string): Promise<void> => {
  const regex = new RegExp(/^.+@([A-zА-Яа-я0-9][-A-zА-Яа-я0-9]+\.)+[A-zА-я]{2,4}$/)
  return regex.test(value)
    ? Promise.resolve()
    : Promise.reject('Электронный адрес введен некорректно (например name@domain.ru)')
}

export const emailRecoveryValidator = async (rule: RuleObject, value: string): Promise<void> => {
  await emailValidator(rule, value)
  if (value.substring(value.length - 4) === '.com') {
    return Promise.reject(
      'Для пользователей с доменной зоной «.com» (например gmail.com, egypt.com и др) необходимо заполнить обращение через форму обратной связи. Мы обработаем в течение одного рабочего дня.',
    )
  }
}

export const emailRuValidator = async (rule: RuleObject, value: string): Promise<void> => {
  await emailValidator(rule, value)
  if (value.substring(value.length - 3) !== '.ru') {
    return Promise.reject('Используйте доменную зону «.ru» (например, rosenergoatom.ru, yandex.ru, mail.ru и пр.)')
  }
}

export const oneRuOrEnLowerCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneLower = /[a-zа-я]/.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneLower) {
    return oneLower ? Promise.resolve() : Promise.reject('Минимум одна строчная буква a-z или а-я.')
  }

  return Promise.resolve()
}

export const oneRuOrEnUpperCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneUpper = /[A-ZА-Я]/.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneUpper) {
    return Promise.reject('Минимум одна заглавная буква A-Z или А-Я.')
  }

  return Promise.resolve()
}

export const oneDigitCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneDigit = /\d/.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneDigit) {
    return Promise.reject('Минимум одна цифра.')
  }

  return Promise.resolve()
}

export const oneLatinLowerCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneLower = /[a-z]/.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneLower) {
    return Promise.reject('Минимум одна строчная латинская буква a-z.')
  }

  return Promise.resolve()
}

export const oneLatinUpperCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneUpper = /[A-Z]/.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneUpper) {
    return Promise.reject('Минимум одна заглавная латинская буква A-Z.')
  }

  return Promise.resolve()
}

export const oneSpecialCharValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const oneSpecial = PASSWORD_SPECIAL_CHAR_REGEX.test(value)
  if (value.length >= MIN_LEN_PASSWORD && !oneSpecial) {
    return Promise.reject('Минимум один спецсимвол.')
  }

  return Promise.resolve()
}

export const latinPasswordValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value) {
    return Promise.resolve()
  }

  const hasInvalidChars = /\s/.test(value) || /[^A-Za-z0-9!-/:-@[-`{-~]/.test(value)
  return hasInvalidChars
    ? Promise.reject('Пароль должен содержать только латинские буквы, цифры и спецсимволы без пробелов.')
    : Promise.resolve()
}

export const weakPasswordPatternsValidator = (rule: RuleObject, value: string): Promise<void> => {
  if (!value || value.length < MIN_LEN_PASSWORD) {
    return Promise.resolve()
  }

  const normalized = value.toLowerCase()
  const normalizedLeet = normalizeLeetPassword(value)
  const hasForbiddenWord = PASSWORD_FORBIDDEN_WORDS.some(
    (word) => normalized.includes(word) || normalizedLeet.includes(word),
  )
  const hasRepeatedChars = /(.)\1{2,}/.test(normalized)

  return hasForbiddenWord || hasRepeatedChars || hasRepeatedGroup(normalized) || hasOrderedSequence(normalized)
    ? Promise.reject(
        'Пароль не должен содержать словарные слова, повторы или легко угадываемые последовательности символов.',
      )
    : Promise.resolve()
}

export const validatorForBirthDate = (rule: RuleObject, value: string): Promise<void> => {
  if (value) {
    const dateParts = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value)
    if (!dateParts) return Promise.reject('Некорректно указана дата')

    const day = Number(dateParts[1])
    const month = Number(dateParts[2])
    const year = Number(dateParts[3])
    const date = new Date(year, month - 1, day)
    const isInvalidDate = date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day
    const isFutureDate = date > new Date()
    const isBefore1900 = date < new Date(1900, 0, 1)

    if (isInvalidDate || isFutureDate || isBefore1900) return Promise.reject('Некорректно указана дата')
  }
  return Promise.resolve()
}

export const validatorForBirthDateWithDot = (rule: RuleObject, value: string): Promise<void> => {
  if (value) {
    const date = new Date(value.toString())

    const day = date.getDate()
    const month = date.getMonth() + 1

    if (day > 31) return Promise.reject('Некорректно указан день')
    if (month > 12 || month === 0) return Promise.reject('Некорректно указан месяц')

    if (date > new Date()) return Promise.reject('День рождения не может быть в будущем')
    if (date < new Date('01.01.1900')) return Promise.reject('Вам слишком много лет')
  } else {
    return Promise.reject('Это поле не должно быть пустым')
  }
  return Promise.resolve()
}
